import React, { useState, useEffect } from "react";
import { Typography, Grid, Card, CardContent } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Box } from "@mui/system";
import { format, addDays, subDays, eachDayOfInterval, startOfWeek, endOfWeek } from "date-fns";
import { Tag, Button } from "antd";
import useScreenSize from "../../util/customHooks/useScreenSize";
import { getEmployeeShiftData } from "../../services/FunctionKeysService";
import { useNavigate } from "react-router-dom";
import { REACT_FUNCTION_KEY_SCREEN } from "../../constants/ReactEndPoints";
import ShiftDetailsModel from "../../components/ShiftDetailsModel";
import { useTranslation } from "react-i18next";

const OnlineSchedule = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useScreenSize();
  const [shiftData, setShiftData] = useState([]);
  const [weekRange, setWeekRange] = useState({
    fromDate: startOfWeek(new Date(), { weekStartsOn: 0 }),
    toDate: endOfWeek(new Date(), { weekStartsOn: 0 }),
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [employeeShiftData, setEmployeeShiftData] = useState({});

  const daysOfWeek = [
    t("onlineSchedule.days.sunday"),
    t("onlineSchedule.days.monday"),
    t("onlineSchedule.days.tuesday"),
    t("onlineSchedule.days.wednesday"),
    t("onlineSchedule.days.thursday"),
    t("onlineSchedule.days.friday"),
    t("onlineSchedule.days.saturday"),
  ];

  useEffect(() => {
    fetchShiftData(weekRange.fromDate, weekRange.toDate);
  }, [weekRange]);

  const fetchShiftData = (from, to) => {
    const fromDate = format(from, "yyyy-MM-dd");
    const toDate = format(to, "yyyy-MM-dd");

    getEmployeeShiftData(fromDate, toDate)
      .then((res) => {
        if (res?.data?.message == "Success") {
          setShiftData(res?.data?.data);
        } else {
          setShiftData([]);
        }
      })
      .catch((error) => {
        console.error("Getting error while fetching the Online Shift details", error);
        setShiftData([]);
      });
  };

  const getShiftsForDate = (date) => {
    return shiftData.filter((shift) => shift.shiftDate === format(date, "MM/dd/yyyy"));
  };

  const handlePreviousWeek = () => {
    setWeekRange((prevRange) => ({
      fromDate: subDays(prevRange.fromDate, 7),
      toDate: subDays(prevRange.toDate, 7),
    }));
  };

  const handleNextWeek = () => {
    setWeekRange((prevRange) => ({
      fromDate: addDays(prevRange.fromDate, 7),
      toDate: addDays(prevRange.toDate, 7),
    }));
  };

  const handleToday = () => {
    setWeekRange({
      fromDate: startOfWeek(new Date(), { weekStartsOn: 0 }),
      toDate: endOfWeek(new Date(), { weekStartsOn: 0 }),
    });
  };

  const weekDates = eachDayOfInterval({
    start: weekRange.fromDate,
    end: weekRange.toDate,
  });

  const handleShiftClick = (shift) => {
    setIsModalOpen(true);
    setEmployeeShiftData(shift);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
      {/* Date Navigation */}
      <Box
        sx={{
          width: isMobile ? "90%" : isTablet ? "60%" : "40%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingTop: "10px",
        }}
      >
        <Button
          className="hover-btn"
          type="default"
          icon={<ArrowBackIosNewIcon />}
          size={"middle"}
          onClick={handlePreviousWeek}
        />
        <Typography>
          {format(weekRange.fromDate, "MM/dd/yyyy")} {t("onlineSchedule.labels.to")} {format(weekRange.toDate, "MM/dd/yyyy")}
        </Typography>

        <Button
          className="hover-btn"
          type="default"
          icon={<ArrowForwardIosIcon />}
          size={"middle"}
          onClick={handleNextWeek}
        />

        <Button className="ant-design-primary-btn" type="primary" variant="solid" size="middle" onClick={handleToday}>
          {t("onlineSchedule.buttons.today")}
        </Button>
      </Box>

      {/* Schedule Table */}
      <Grid
        container
        sx={{
          p: 2,
          maxHeight: isMobile ? "60%" : "100%",
          overflowY: "auto",
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#999",
            borderRadius: "10px",
          },
        }}
      >
        {weekDates.map((date, index) => (
          <Grid item xs={4} sm={12 / 7} md={12 / 7} lg={12 / 7} key={date}>
            <Card
              sx={{
                height: "180px",
              }}
            >
              <CardContent
                sx={{
                  padding: "0px",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    backgroundColor: "#023A59",
                    marginBottom: "10px",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      textAlign: "center",
                      color: "white",
                    }}
                  >
                    {daysOfWeek[index]}
                  </Typography>
                </Box>

                {/* Shift Time Tags (Scrollable) */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "8px",
                    maxHeight: "120px",
                    overflowY: "auto",
                    scrollbarWidth: "thin",
                    "&::-webkit-scrollbar": {
                      width: "5px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#999",
                      borderRadius: "10px",
                    },
                  }}
                >
                  {getShiftsForDate(date).length > 0 ? (
                    getShiftsForDate(date).map((shift, i) => (
                      <Tag
                        bordered={false}
                        color="processing"
                        style={{
                          width: "90%",
                          textAlign: "center",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          marginInlineEnd: "0px",
                          cursor: "pointer",
                        }}
                        key={i}
                        onClick={() => handleShiftClick(shift)}
                      >
                        {isMobile || isTablet ? (
                          <>
                            {shift.startTime} <br />
                            {t("onlineSchedule.labels.to")}
                            <br /> {shift.endTime}
                          </>
                        ) : (
                          `${shift.startTime} ${t("onlineSchedule.labels.to")} ${shift.endTime}`
                        )}
                      </Tag>
                    ))
                  ) : (
                    <Tag
                      bordered={false}
                      color="processing"
                      style={{
                        width: "90%",
                        textAlign: "center",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      -
                    </Tag>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ position: "absolute", right: "15px", bottom: "10px", display: "flex" }}>
        <Button
          className="ant-design-primary-btn"
          style={{
            width: "100px",
          }}
          type="primary"
          variant="solid"
          size="middle"
          onClick={() => navigate(REACT_FUNCTION_KEY_SCREEN)}
        >
          {t("onlineSchedule.buttons.exit")}
        </Button>
      </Box>

      <ShiftDetailsModel
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        employeeShiftData={employeeShiftData}
        showDetailsIfEmployeeUnderShift={false}
      />
    </Box>
  );
};

export default OnlineSchedule;
